import React from 'react';

export interface ModelStats {
  forecastCount: number;
  observationCount?: number;
  meanObsCoverage?: number;
  kge?: {mean?: number; p10?: number; p90?: number};
  crps?: {mean?: number; p10?: number; p90?: number};
  pbias?: {mean?: number};
  coverage80?: {mean?: number; p10?: number; p90?: number};
  nrmse?: {mean?: number};
  mae?: {mean?: number};
  // Skill comparison values
  kgePersistence?: number;
  kgeLta?: number;
  crpsPersistence?: number;
  crpsLta?: number;
  nrmsePersistence?: number;
  nrmseLta?: number;
  maePersistence?: number;
  maeLta?: number;
}

function formatValue(v: number | undefined | null, decimals: number = 2): string {
  if (v === undefined || v === null || isNaN(v)) return 'N/A';
  return v.toFixed(decimals);
}

function formatPercent(v: number | undefined | null): string {
  if (v === undefined || v === null || isNaN(v)) return 'N/A';
  return `${(v * 100).toFixed(1)}%`;
}

interface SkillIndicatorProps {
  value?: number;
  baseline?: number;
  label: string;
  higherIsBetter?: boolean;
}

function SkillIndicator({value, baseline, label, higherIsBetter = true}: SkillIndicatorProps) {
  if (value === undefined || value === null || baseline === undefined || baseline === null) {
    return null;
  }
  if (isNaN(value) || isNaN(baseline)) return null;

  const diff = higherIsBetter ? value - baseline : baseline - value;
  const pct = Math.abs(diff * 100).toFixed(0);
  const isPositive = diff > 0;
  const arrow = isPositive ? '▲' : '▼';
  const colorClass = isPositive ? 'text-green-600' : 'text-red-500';

  return (
    <span
      className={`text-xs ${colorClass}`}
      title={`${isPositive ? 'Better' : 'Worse'} than ${label}`}
    >
      {arrow}
      {pct}% <span className="text-gray-400">{label}</span>
    </span>
  );
}

interface StatCardProps {
  label: string;
  value: string;
  detail?: React.ReactNode;
  skills?: React.ReactNode;
}

function StatCard({label, value, detail, skills}: StatCardProps) {
  return (
    <div className="flex flex-col">
      <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">{label}</div>
      <div className="mt-1 text-2xl font-bold text-gray-900">{value}</div>
      {detail && <div className="mt-1 text-xs text-gray-500">{detail}</div>}
      {skills && <div className="mt-1 flex gap-2 flex-wrap">{skills}</div>}
    </div>
  );
}

export default function ModelStatsPanel({stats}: {stats: ModelStats}) {
  const kgeMean = stats.kge?.mean;
  const crpsMean = stats.crps?.mean;
  const pbiasMean = stats.pbias?.mean;
  const nrmseMean = stats.nrmse?.mean;
  const maeMean = stats.mae?.mean;
  // const cov80Mean = stats.coverage80?.mean;

  return (
    <div className="space-y-4">
      {/* Forecast and observation counts */}
      <div className="text-sm text-gray-600">
        Based on {stats.forecastCount} forecast{stats.forecastCount !== 1 ? 's' : ''}
        {stats.observationCount != null && stats.observationCount > 0 && (
          <>
            {' '}
            ({stats.observationCount.toLocaleString()} observations,{' '}
            {formatPercent(stats.meanObsCoverage)} coverage)
          </>
        )}
      </div>

      {/* Metrics grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-6">
        <StatCard
          label="KGE"
          value={formatValue(kgeMean, 3)}
          skills={
            <>
              <SkillIndicator
                value={kgeMean}
                baseline={stats.kgePersistence}
                label="PER"
                higherIsBetter={true}
              />
              <SkillIndicator
                value={kgeMean}
                baseline={stats.kgeLta}
                label="LTA"
                higherIsBetter={true}
              />
            </>
          }
        />
        <StatCard
          label="CRPS"
          value={formatValue(crpsMean, 1)}
          skills={
            stats.crpsPersistence !== undefined || stats.crpsLta !== undefined ? (
              <>
                <SkillIndicator
                  value={crpsMean}
                  baseline={stats.crpsPersistence}
                  label="PER"
                  higherIsBetter={false}
                />
                <SkillIndicator
                  value={crpsMean}
                  baseline={stats.crpsLta}
                  label="LTA"
                  higherIsBetter={false}
                />
              </>
            ) : undefined
          }
        />
        <StatCard
          label="nRMSE"
          value={formatValue(nrmseMean, 3)}
          skills={
            <>
              <SkillIndicator
                value={nrmseMean}
                baseline={stats.nrmsePersistence}
                label="PER"
                higherIsBetter={false}
              />
              <SkillIndicator
                value={nrmseMean}
                baseline={stats.nrmseLta}
                label="LTA"
                higherIsBetter={false}
              />
            </>
          }
        />
        <StatCard
          label="MAE"
          value={formatValue(maeMean, 1)}
          skills={
            <>
              <SkillIndicator
                value={maeMean}
                baseline={stats.maePersistence}
                label="PER"
                higherIsBetter={false}
              />
              <SkillIndicator
                value={maeMean}
                baseline={stats.maeLta}
                label="LTA"
                higherIsBetter={false}
              />
            </>
          }
        />
        <StatCard
          label="PERCENT BIAS"
          value={pbiasMean !== undefined ? `${formatValue(pbiasMean, 1)}%` : 'N/A'}
          detail="0% = unbiased"
        />
        {/* TODO: re-enable once coverage80 data is ready
        <StatCard
          label="80% COVERAGE"
          value={formatPercent(cov80Mean)}
          detail="Target: 80%"
        />
        */}
      </div>
    </div>
  );
}
